/* ===========================================================
   Lucraa — Landing · scroll-reveal (progressive enhancement)
   Adiciona `js-reveal` ao <html> só quando o JS roda e revela cada
   `.lp-stagger` ao entrar na viewport (filhos em cascata via --i).
   SEM JS ou com prefers-reduced-motion, o conteúdo já nasce visível
   (CSS .lp-stagger > * { opacity: 1 }) — nunca esconde nada.
   Com JS, o `js-reveal` deixa os alvos em opacity:0 ATÉ ganharem
   `.is-in`. Como esconder e revelar ficam em mãos diferentes (CSS x
   IntersectionObserver), um IO que não dispara — webview/preview que
   não recalcula no scroll, browser exótico, falha do observer —
   prenderia o conteúdo invisível. Duas redes de segurança evitam isso:
   (1) o que já está na viewport no mount é revelado na hora;
   (2) um timeout de segurança revela tudo que sobrar. Em browser real
   o IO chega primeiro e o stagger por scroll continua intacto.
   =========================================================== */
import { useEffect } from "react";

/** Tempo até a rede de segurança revelar alvos que o IO não tocou. */
const REVELAR_FAILSAFE_MS = 2000;

/**
 * Liga o reveal staggered nas seções `.lp-stagger` da landing.
 *
 * `prontidao` é um sinal que MUDA quando os alvos entram no DOM — na landing,
 * as seções vivem sob `{D && …}` e só montam quando os dados chegam (async).
 * O efeito só enxerga `.lp-stagger` que já existem quando roda; sem re-rodar
 * nesse sinal, as seções montadas depois ficariam presas em opacity:0.
 * Passe algo estável como `Boolean(dados)` (não a referência crua, que pode
 * mudar a cada render e recriar o observer à toa).
 */
export function useReveal(prontidao?: unknown) {
  useEffect(() => {
    const root = document.documentElement;
    const reduz = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    // sem IntersectionObserver ou com motion reduzido: deixa tudo visível (CSS base).
    if (reduz || typeof IntersectionObserver === "undefined") return;

    root.classList.add("js-reveal");
    const alvos = Array.from(document.querySelectorAll<HTMLElement>(".lp-stagger"));
    for (const alvo of alvos) {
      Array.from(alvo.children).forEach((filho, i) => {
        if (filho instanceof HTMLElement) filho.style.setProperty("--i", String(i));
      });
    }

    const revelar = (alvo: Element) => alvo.classList.add("is-in");
    const naViewport = (alvo: HTMLElement) => {
      const r = alvo.getBoundingClientRect();
      return r.top < window.innerHeight && r.bottom > 0;
    };

    const io = new IntersectionObserver(
      (entradas) => {
        for (const e of entradas) {
          if (e.isIntersecting) {
            revelar(e.target);
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.1 },
    );
    for (const alvo of alvos) io.observe(alvo);

    // rede 1: o que já está visível no mount aparece na hora, sem depender do IO.
    for (const alvo of alvos) if (naViewport(alvo)) revelar(alvo);

    // rede 2: nada pode ficar preso em opacity:0 — revela o que sobrou.
    const failsafe = window.setTimeout(() => {
      for (const alvo of alvos) revelar(alvo);
    }, REVELAR_FAILSAFE_MS);

    return () => {
      window.clearTimeout(failsafe);
      io.disconnect();
      root.classList.remove("js-reveal");
    };
  }, [prontidao]);
}
